import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { setBusinessArea, setFolder1, setFolder2, setFolder3, setProperty } from '../redux/modules/appSlice';
import { selectBusinessAreaById } from '../redux/modules/data/businessAreasSlice';
import { selectFolderById, selectFolderByIdWithObjectsAndProperties } from '../redux/modules/data/foldersSlice';
import { selectPropertyById } from '../redux/modules/data/propertiesSlice';

const useAnalyzeRoute = () => {
	const {
		baId, folder1Id, folder2Id, folder3Id, propertyId,
	} = useParams();
	const dispatch = useDispatch();

	const businessArea = useSelector((state) => selectBusinessAreaById(state, baId));
	const folder1 = useSelector((state) => selectFolderById(state, folder1Id));
	const folder2 = useSelector((state) => selectFolderByIdWithObjectsAndProperties(state, folder2Id));
	const folder3 = useSelector((state) => selectFolderByIdWithObjectsAndProperties(state, folder3Id));
	const selectedProperty = useSelector((state) => selectPropertyById(state, propertyId));

	const routeInfo = {
		redirectPath: null,
		activeTree: {
			businessArea: null,
			folder1: null,
			folder2: null,
			folder3: null,
			property: null,
		},
	};

	useEffect(() => {
		if (baId && businessArea) {
			dispatch(setBusinessArea({ businessArea }));
		} else {
			dispatch(setBusinessArea({ businessArea: null }));
		}
	}, [baId, businessArea, dispatch]);

	useEffect(() => {
		if (baId && businessArea && folder1Id && folder1 && folder2Id && folder2 && businessArea.children.includes(folder1.id) && folder1.children.includes(folder2.id)) {
			dispatch(setFolder1({ folder1 }));
			dispatch(setFolder2({ folder2 }));
		} else {
			dispatch(setFolder1({ folder1: null }));
			dispatch(setFolder2({ folder2: null }));
		}
	}, [baId, businessArea, folder1Id, folder1, folder2Id, folder2, dispatch]);

	useEffect(() => {
		if (baId && businessArea && folder1Id && folder1 && folder2Id && folder2 && businessArea.children.includes(folder1.id) && folder1.children.includes(folder2.id) && folder3Id && folder3 && folder2.children.includes(folder3.id)) {
			dispatch(setFolder3({ folder3 }));
		} else {
			dispatch(setFolder3({ folder3: null }));
		}
	}, [baId, businessArea, folder1Id, folder1, folder2Id, folder2, folder3Id, folder3, dispatch]);

	useEffect(() => {
		if (baId && businessArea && folder1Id && folder1 && folder2Id && folder2 && businessArea.children.includes(folder1.id) && folder1.children.includes(folder2.id) && !folder3Id && !folder3 && folder2.children.length === 0 && propertyId && selectedProperty && folder2.propertyIds?.includes(propertyId)) {
			dispatch(setProperty({ property: selectedProperty }));
		} else if (baId && businessArea && folder1Id && folder1 && folder2Id && folder2 && businessArea.children.includes(folder1.id) && folder1.children.includes(folder2.id) && folder3Id && folder3 && folder2.children.includes(folder3.id) && propertyId && selectedProperty && folder3.propertyIds?.includes(propertyId)) {
			dispatch(setProperty({ property: selectedProperty }));
		} else {
			dispatch(setProperty({ property: null }));
		}
	}, [baId, businessArea, folder1Id, folder1, folder2Id, folder2, folder3Id, folder3, propertyId, selectedProperty, dispatch]);

	useEffect(() => () => {
		dispatch(setBusinessArea({ businessArea: null }));
		dispatch(setFolder1({ folder1: null }));
		dispatch(setFolder2({ folder2: null }));
		dispatch(setFolder3({ folder3: null }));
		dispatch(setProperty({ property: null }));
	}, [dispatch]);

	if (!baId) {
		// No BA set -> no further params to analyze
		return { ...routeInfo };
	}

	if (baId && !businessArea) {
		// Wrong BA set -> redirect to Home
		return { ...routeInfo, redirectPath: '/' };
	}
	routeInfo.activeTree.businessArea = businessArea;

	if (!folder1) {
		// BA is set, but no subfolders -> load BA screen
		return routeInfo;
	}

	if (((folder1Id && !folder1) || (folder2Id && !folder2))) {
		//  BA is set, folder 1 or folder 2 is not correct -> redirect to BA screen
		return { ...routeInfo, redirectPath: `/${businessArea.id}` };
	}

	// Check if folder 1 and 2 are children in tree
	if (businessArea.children.includes(folder1.id) && folder1.children.includes(folder2.id)) {
		routeInfo.activeTree.folder1 = folder1;
		routeInfo.activeTree.folder2 = folder2;
	} else {
		// folder 1 or 2 are not children in the tree -> redirect to BA screen
		return { ...routeInfo, redirectPath: `/${businessArea.id}` };
	}

	if (folder3Id && !folder3) {
		// folder 3 is not correct -> redirect to folder 2
		return { ...routeInfo, redirectPath: `/${businessArea.id}/${folder1.id}/${folder2.id}` };
	}

	if (!folder3) {
		// No folder 3 set
		if (folder2.children.length > 0) {
			// Folder2 has children -> show folder 2 screen
			return routeInfo;
		}
		// folder 2 has no children
		if (propertyId && selectedProperty && folder2.propertyIds?.includes(propertyId)) {
			// property is set and is correct
			routeInfo.activeTree.property = selectedProperty;
			return routeInfo;
		}

		const firstPropertyOfFolder = folder2.properties?.[0];
		if (firstPropertyOfFolder) {
			return { ...routeInfo, redirectPath: `/${businessArea.id}/${folder1.id}/${folder2.id}/in-${firstPropertyOfFolder.id}` };
		}

		// Folder 2 has no properties and no objects -> redirect to BA
		return { ...routeInfo, redirectPath: `/${businessArea.id}` };
	}

	// Check if folder 3 is a child of folder 2 -> all ok show folder 3 screen
	if (folder2.children.includes(folder3.id)) {
		routeInfo.activeTree.folder3 = folder3;
	} else {
		// folder 3 is not child in the tree -> redirect to folder 2 screen
		return { ...routeInfo, redirectPath: `/${businessArea.id}/${folder1.id}/${folder2.id}` };
	}

	if (propertyId && selectedProperty && folder3.propertyIds?.includes(propertyId)) {
		// property is set and is correct
		routeInfo.activeTree.property = selectedProperty;
		return routeInfo;
	}

	const firstPropertyOfFolder = folder3.properties?.[0];
	if (firstPropertyOfFolder) {
		return { ...routeInfo, redirectPath: `/${businessArea.id}/${folder1.id}/${folder2.id}/${folder3.id}/in-${firstPropertyOfFolder.id}` };
	}

	// Folder 3 has no properties and no objects -> redirect to folder2
	return { ...routeInfo, redirectPath: `/${businessArea.id}/${folder1.id}/${folder2.id}` };
};

export default useAnalyzeRoute;
